import { ErrorMessage } from "@hookform/error-message";
import React, { useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import "react-medium-image-zoom/dist/styles.css";
import { NumericFormat } from "react-number-format";
import { useHistory, useParams } from "react-router";
import { toast } from "react-toastify";
import { Input } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Loading,
  Row,
  RSelect,
} from "../../components/Component";
import BillPicture from "../../components/picture/Picture";
import { useUser } from "../../context/user";
import { GET, POST, PUT } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { BILL_TYPE, getDefaultBill } from "../../utils/Utils";

const BILL_TYPE_OPTIONS = Object.keys(BILL_TYPE).map((c) => ({
  label: BILL_TYPE[c],
  value: c,
}));

const PNBillUpdateByWeighingSlip = () => {
  const history = useHistory();
  const { encryptData } = useParams();
  const { state: {
    user
  } } = useUser();
  const methodForm = useForm({
    defaultValues: {
      ...getDefaultBill()
    },
    shouldFocusError: true,

  });
  const {
    errors,
    register,
    handleSubmit,
    reset,
    getValues,
    control,
    setValue,
  } = methodForm;
  const [loading, setLoading] = useState(false);

  const [smOption, setSmOption] = useState(false);
  const [driverOptions, setDriverOptions] = useState([]);
  const [billData, setBillData] = useState();
  const [scanStatus, setScanStatus] = useState("idle"); // idle | scanning | success | error
  const [extractingSrc, setExtractingSrc] = useState(null);
  const [extractClickCount, setExtractClickCount] = useState(0);
  const [manualEntryEnabled, setManualEntryEnabled] = useState(false);
  const [extractedSrcs, setExtractedSrcs] = useState([]);
  const [aiExtractedWeights, setAiExtractedWeights] = useState(null);

  const weightInputsRef = useRef(null);

  const truckWeight = useWatch({
    control,
    name: "truckWeight",
  });

  const totalWeight = useWatch({
    control,
    name: "totalWeight",
  });


  useEffect(() => {
    setValue("merchandiseWeight", +(totalWeight || 0) - +(truckWeight || 0));
  }, [truckWeight, totalWeight]);


  useEffect(() => {

    GET("/user/list-drivers", {}, true).then((resp) => {
      const { data } = resp;
      setDriverOptions(
        data.map((c) => ({
          label: c.name,
          value: c.id,
          data: c,
        }))
      );
    });


    GET(`/pn-bill-for-driver/scan/${encryptData}`, {}, true)
      .then((resp) => {
        let { data: { bill: data } } = resp;
        reset(data);
        setBillData(data);
        if (data.type === 'OUT') {
          setValue("truckWeight", data.destinationTruckWeight);
          setValue("totalWeight", data.destinationTotalWeight);
          setValue("merchandiseWeight", data.destinationMerchandiseWeight);
          setValue("merchandiseWeightAt", data.destinationMerchandiseWeightAt);
        } else {
          setValue("truckWeight", data.departureTruckWeight);
          setValue("totalWeight", data.departureTotalWeight);
          setValue("merchandiseWeight", data.departureMerchandiseWeight);
          setValue("merchandiseWeightAt", data.departureMerchandiseWeightAt);
        }
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại 1."
        );
      })
  }, []);

  const markSrcExtracted = (mediaUrl) => {
    setExtractedSrcs((prev) => (prev.includes(mediaUrl) ? prev : [...prev, mediaUrl]));
  };

  const handleExtractWeight = async (mediaUrl) => {
    if (extractedSrcs.includes(mediaUrl)) {
      toast.error(
        <span style={{ fontSize: 18, fontWeight: 700 }}>
          Ảnh này đã được phân tích trước đó! Vui lòng chụp ảnh phiếu cân mới.
        </span>
      );
      return;
    }

    const nextClickCount = extractClickCount + 1;
    setExtractClickCount(nextClickCount);
    if (nextClickCount > 2) {
      setManualEntryEnabled(true);
    }

    setExtractingSrc(mediaUrl);
    setScanStatus("scanning");
    try {
      const { data } = await POST(
        "/pn-bill-for-driver/extract-weighing-slip",
        { imageUrl: mediaUrl },
        {},
        true
      );

      if (data.truckWeight == null && data.totalWeight == null) {
        setScanStatus("error");
        markSrcExtracted(mediaUrl);
        toast.error(
          "Không đọc được số liệu từ ảnh. Vui lòng chụp lại rõ nét hơn."
        );
        return;
      }

      if (data.truckWeight != null) setValue("truckWeight", data.truckWeight);
      if (data.totalWeight != null) setValue("totalWeight", data.totalWeight);
      setAiExtractedWeights({ truckWeight: data.truckWeight, totalWeight: data.totalWeight });
      setScanStatus("success");
      markSrcExtracted(mediaUrl);
      toast.success(
        <span>
          Đã đọc được từ ảnh: KL xe không tải{" "}
          <strong>
            {data.truckWeight != null ? data.truckWeight.toLocaleString() : "?"} kg
          </strong>{" "}
          - KL xe có tải{" "}
          <strong>
            {data.totalWeight != null ? data.totalWeight.toLocaleString() : "?"} kg
          </strong>
          . Nếu sai, vui lòng chụp ảnh phiếu cân khác rõ nét hơn.
        </span>
      );
      // Đợi render xong (ẩn alert lỗi, tắt trạng thái đang quét) rồi mới scroll, tránh bị lệch vị trí trên điện thoại
      setTimeout(() => {
        weightInputsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    } catch ({ response }) {
      setScanStatus("error");
      // 400 = ảnh không phân tích được số liệu -> bắt buộc chụp ảnh mới, không cho phân tích lại ảnh cũ
      if (response?.status === 400) {
        markSrcExtracted(mediaUrl);
      }
      toast.error(
        response?.data?.error ||
        "Có lỗi xảy ra khi phân tích ảnh! Vui lòng thử lại."
      );
    } finally {
      setExtractingSrc(null);
    }
  };

  const onHandleSubmit = () => {
    setLoading(true);
    const {
      totalWeight,
      merchandiseWeight,
      truckWeight,
      subDriverId,
      billPictures,
    } = getValues();

    // Chỉ tính là nhập tay khi số liệu gửi lên khác với kết quả AI đọc được lần gần nhất
    const isSameAsAiExtracted =
      !!aiExtractedWeights &&
      Number(truckWeight) === Number(aiExtractedWeights.truckWeight) &&
      Number(totalWeight) === Number(aiExtractedWeights.totalWeight);

    const formData = {
      driverId: user.id,
      subDriverId: subDriverId || null,

      totalWeight,
      merchandiseWeight,

      truckWeight,
      merchandiseWeightAt: new Date(),
      truckWeightAt: new Date(),
      billPictures: billPictures?.map((c) => c.src) || [],
      isManualWeightEntry: manualEntryEnabled && !isSameAsAiExtracted,
    };

    const request = PUT(
      `/pn-bill-for-driver/upsert-pn-bill-from-weighing-slip/${encryptData}`,
      formData,
      {},
      true
    );

    request
      .then((res) => {
        toast.success("Cập nhật thành công");
        history.goBack();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!billData) {
    return <Loading />
  }
  return (
    <React.Fragment>
      <Head title="Đơn hàng"></Head>

      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Đơn hàng</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <div>
                        <Button
                          color="primary"
                          onClick={() => {
                            history.goBack();
                          }}
                        >
                          <span>Trở về</span>
                        </Button>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div
                id="DataTables_Table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4 no-footer"
              >
                <div className="row justify-between g-2">
                  <div className="col-8 col-sm-6 text-start"></div>
                  <div className="col-5 col-sm-6 text-end">
                    <div className="datatable-filter">
                      <div className="d-flex justify-content-end g-2">
                        <div
                          className="dataTables_length"
                          id="DataTables_Table_1_length"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="my-3">
                  <FormProvider {...methodForm}>
                    <form onSubmit={handleSubmit(onHandleSubmit)}>
                      <div className="mb-4">
                        <Row className="g-2">
                          <Col size={12}>
                            <BlockTitle tag="h5">
                              Thông tin khách hàng
                            </BlockTitle>
                          </Col>
                          <Col size={6}>
                            <div className="form-group">
                              <label
                                className="form-label"
                                htmlFor="customer"
                              >
                                Mã khách hàng{" "}
                              </label>
                              <div className="form-control-wrap">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={billData?.customer?.id || ""}
                                  disabled
                                />
                              </div>
                            </div>
                          </Col>
                          <Col size={6}>
                            <div className="form-group">
                              <label
                                className="form-label"
                                htmlFor="customer"
                              >
                                Tên khách hàng{" "}
                              </label>
                              <div className="form-control-wrap">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={billData?.customer?.name || ""}
                                  disabled
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      <Row className="mt-3 g-2">
                        <Col size={12}>
                          <BlockTitle tag="h5">Thông tin đơn hàng</BlockTitle>
                        </Col>
                        <Col size="6">
                          <div className="form-group">
                            <label className="form-label" htmlFor="customer">
                              Mã đơn hàng <span className="text-danger">*</span>
                            </label>
                            <div className="form-control-wrap">
                              <input
                                type="text"
                                className="form-control"
                                name="id"
                                ref={register({
                                  required: "Vui lòng nhập thông tin",
                                })}
                                disabled={true}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="id"
                                render={({ message }) => (
                                  <span className="invalid">{message}</span>
                                )}
                              />
                            </div>
                          </div>
                        </Col>

                        <Col size="6">
                          <div className="form-group">
                            <label className="form-label" >
                              Loại đơn<span className="text-danger">*</span>
                            </label>
                            <div className="form-control-wrap">
                              <Controller
                                control={control}
                                name="type"
                                rules={{ required: "Vui lòng nhập thông tin" }}
                                render={({ onChange, value, name, ref }) => (
                                  <RSelect
                                    inputRef={ref}
                                    name={name}
                                    options={BILL_TYPE_OPTIONS}
                                    value={BILL_TYPE_OPTIONS.find(
                                      (c) => c.value === value
                                    )}
                                    onChange={(val) => onChange(val.value)}
                                    isDisabled={true}
                                  />
                                )}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="type"
                                render={({ message }) => (
                                  <span className="invalid">{message}</span>
                                )}
                              />
                            </div>
                          </div>
                        </Col>
                        <Col size="6">
                          <div className="form-group">
                            <label className="form-label" >
                              Xe tải <span className="text-danger">*</span>
                            </label>
                            <div className="form-control-wrap">
                              <Input
                                type="text"
                                className="form-control"
                                value={billData?.truckId}
                                disabled
                              />
                            </div>
                          </div>
                        </Col>

                        <Col md="6">
                          <div className="form-group">
                            <label className="form-label" >
                              Tài phụ
                            </label>
                            <div className="form-control-wrap">
                              <Controller
                                control={control}
                                name="subDriverId"
                                render={({ onChange, value, name, ref }) => (
                                  <RSelect
                                    isClearable
                                    ref={ref}
                                    name={name}
                                    options={driverOptions}
                                    value={driverOptions.find((c) => c.value === value) || null}
                                    onChange={(val) => {
                                      onChange(val?.value || '');
                                    }}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <div className="mt-4">
                        <Row className="g-2">
                          <Col size={12}>
                            <BlockTitle tag="h5">Nhập dữ liệu cân</BlockTitle>
                            <span className="text-soft">
                              Chụp hoặc tải hình phiếu cân xe bên dưới, sau đó bấm "Xuất KL từ ảnh" dưới ảnh để tự động đọc khối lượng.
                              {manualEntryEnabled
                                ? " Bạn đã bấm xuất quá 2 lần, có thể nhập tay khối lượng bên dưới."
                                : " Nếu bấm xuất quá 2 lần mà không đúng, hệ thống sẽ cho phép nhập tay."}
                            </span>
                          </Col>

                          {scanStatus === "error" && (
                            <Col size={12}>
                              <div className="alert alert-danger mb-0">
                                Không đọc được số liệu từ ảnh. Vui lòng chụp lại rõ nét hơn rồi bấm "Xuất KL từ ảnh".
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>

                      <div className="mt-4" ref={weightInputsRef}>
                        <Row className="g-2">
                          <Col size="6">
                            <div className="form-group">
                              <label className="form-label" >
                                KL xe không tải(kg)
                              </label>
                              <div className="form-control-wrap">
                                <Controller
                                  control={control}
                                  name="truckWeight"
                                  rules={{
                                    required: "Vui lòng nhập thông tin",
                                  }}
                                  render={({ onChange, value, ref }) => (
                                    <NumericFormat
                                      className="form-control"
                                      inputRef={ref}
                                      type="text"
                                      value={value}
                                      thousandsGroupStyle="thousand"
                                      thousandSeparator=","
                                      decimalScale={2}
                                      onValueChange={(val) =>
                                        onChange(val.floatValue)
                                      }
                                      disabled={!manualEntryEnabled}
                                    />
                                  )}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  name="truckWeight"
                                  render={({ message }) => (
                                    <span className="invalid">{message}</span>
                                  )}
                                />
                              </div>
                            </div>
                          </Col>

                          <Col size="6">
                            <div className="form-group">
                              <label className="form-label" >
                                KL hàng(kg)
                              </label>
                              <div className="form-control-wrap">
                                <Controller
                                  control={control}
                                  name="merchandiseWeight"
                                  rules={{
                                    required: "Vui lòng nhập thông tin",
                                  }}
                                  render={({ onChange, value, ref }) => (
                                    <NumericFormat
                                      className="form-control"
                                      inputRef={ref}
                                      type="text"
                                      value={value}
                                      thousandsGroupStyle="thousand"
                                      thousandSeparator=","
                                      decimalScale={2}
                                      onValueChange={(val) =>
                                        onChange(val.floatValue)
                                      }
                                      disabled
                                    />
                                  )}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  name="merchandiseWeight"
                                  render={({ message }) => (
                                    <span className="invalid">{message}</span>
                                  )}
                                />
                              </div>
                            </div>
                          </Col>

                          <Col size="6">
                            <div className="form-group">
                              <label className="form-label" >
                                KL Xe có tải(kg)
                              </label>
                              <div className="form-control-wrap">
                                <Controller
                                  control={control}
                                  name="totalWeight"
                                  rules={{
                                    required: "Vui lòng nhập thông tin",
                                  }}
                                  render={({ onChange, value, ref }) => (
                                    <NumericFormat
                                      className="form-control"
                                      inputRef={ref}
                                      type="text"
                                      value={value}
                                      thousandsGroupStyle="thousand"
                                      thousandSeparator=","
                                      decimalScale={2}
                                      onValueChange={(val) =>
                                        onChange(val.floatValue)
                                      }
                                      disabled={!manualEntryEnabled}
                                    />
                                  )}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  name="totalWeight"
                                  render={({ message }) => (
                                    <span className="invalid">{message}</span>
                                  )}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      <Row className="mt-4">
                        <Col size={12}>
                          <BillPicture
                            onExtractWeight={handleExtractWeight}
                            extractingSrc={extractingSrc}
                          />
                        </Col>
                      </Row>

                      <Row className="g-3 ">
                        <Col size="6 " className="mt-3">
                          <Button
                            color="primary"
                            type="submit"
                            disabled={scanStatus === "scanning"}
                          >
                            <span>Lưu đơn hàng</span>
                          </Button>
                        </Col>
                      </Row>
                    </form>
                  </FormProvider>
                </div>
              </div>
            </div>
          </div>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default PNBillUpdateByWeighingSlip;
